/**
 * Closed list of body site codes accepted by the AIHS4 severity assessment
 * endpoint (see endpoint.md). Kept here as a union so the regions file and the
 * API hook stay in sync.
 */
export type AihsBodySiteCode =
  | 'headFront'
  | 'headBack'
  | 'headTop'
  | 'headLeft'
  | 'headRight'
  | 'armLeft'
  | 'armRight'
  | 'trunkFront'
  | 'trunkBack'
  | 'legLeft'
  | 'legRight'
  | 'handLeft'
  | 'handRight'
  | 'footLeft'
  | 'footRight'
  | 'genital'
  | 'nails'
  | 'scalp'
  | 'earLeft'
  | 'earRight'
  | 'perioral'
  | 'tongue'
  | 'elbowLeft'
  | 'elbowRight'
  | 'upperArmLeft'
  | 'upperArmRight'
  | 'lowerArmLeft'
  | 'lowerArmRight'
  | 'kneeLeft'
  | 'kneeRight'
  | 'upperLegLeft'
  | 'upperLegRight'
  | 'lowerLegLeft'
  | 'lowerLegRight';

export interface ProtocolRegion {
  id: string;
  name: string;
  number: number;
  instructions: string[];
  tips: string[];
  bodySiteCode: AihsBodySiteCode;
}

/**
 * Region data for the Hidradenitis Suppurativa photography protocol.
 *
 * Each region captures exactly one image. The list of regions, instructions
 * and tips is taken verbatim from the sales director's mockup that was
 * validated by the client.
 */
export const HS_PROTOCOL_REGIONS: ProtocolRegion[] = [
  {
    id: 'face',
    name: 'Face',
    number: 1,
    instructions: [
      'Frontal view of the face',
      'Capture jawline and periauricular region if involved'
    ],
    tips: ['Ensure uniform lighting across the face', 'Patient facing the camera directly'],
    bodySiteCode: 'headFront'
  },
  {
    id: 'neck',
    name: 'Neck',
    number: 2,
    instructions: [
      'Capture left and right lateral views of the neck',
      'Ensure both sides are documented to cover the entire area'
    ],
    tips: [
      'Tilt head slightly back for better exposure',
      'Patient should turn head to expose each side'
    ],
    bodySiteCode: 'trunkFront'
  },
  {
    id: 'left-axilla',
    name: 'Left Axilla',
    number: 3,
    instructions: [
      'Patient with left arm elevated (~90°)',
      'Capture axillary vault',
      'Include posterior axillary fold',
      'Include lateral chest if lesions extend'
    ],
    tips: ['Ensure the entire axillary fold is visible', 'Move camera closer rather than zooming'],
    bodySiteCode: 'upperArmLeft'
  },
  {
    id: 'right-axilla',
    name: 'Right Axilla',
    number: 4,
    instructions: [
      'Patient with right arm elevated (~90°)',
      'Capture axillary vault',
      'Include posterior axillary fold',
      'Include lateral chest if lesions extend'
    ],
    tips: ['Same positioning as left axilla', 'Maintain consistent distance (30–50 cm)'],
    bodySiteCode: 'upperArmRight'
  },
  {
    id: 'chest-inframammary',
    name: 'Chest & Inframammary',
    number: 5,
    instructions: ['Include submammary fold', 'Include lateral chest extension'],
    tips: ['Lift breast tissue gently to expose folds'],
    bodySiteCode: 'trunkFront'
  },
  {
    id: 'abdomen',
    name: 'Abdomen',
    number: 6,
    instructions: [
      'Capture all possible skin folds if present',
      'Include periumbilical region',
      'Include lower abdominal folds'
    ],
    tips: ['Patient standing if possible', 'Document each significant skin fold separately'],
    bodySiteCode: 'trunkFront'
  },
  {
    id: 'back',
    name: 'Trunk (Back)',
    number: 7,
    instructions: [
      'Take 1 picture of the entire back',
      'Focus on interscapular and lumbar areas if lesions present'
    ],
    tips: ['Patient standing for the shot', 'Move closer for target lesion images'],
    bodySiteCode: 'trunkBack'
  },
  {
    id: 'left-buttock',
    name: 'Left Buttock',
    number: 8,
    instructions: [
      'Patient standing or prone',
      'Include gluteal fold',
      'Include perianal extension if visible'
    ],
    tips: [
      'Ensure adequate exposure without unnecessary areas',
      'Consistent positioning with right buttock'
    ],
    bodySiteCode: 'trunkBack'
  },
  {
    id: 'right-buttock',
    name: 'Right Buttock',
    number: 9,
    instructions: [
      'Same positioning as left buttock',
      'Include gluteal fold',
      'Include perianal extension if visible'
    ],
    tips: ['Mirror the positioning of the left buttock image'],
    bodySiteCode: 'trunkBack'
  },
  {
    id: 'left-inguinal',
    name: 'Left Inguino-crural Fold',
    number: 10,
    instructions: [
      'Capture left inguino-crural fold + proximal thigh',
      'Include groin fold',
      'Include upper medial thigh'
    ],
    tips: ['Patient standing with slight leg abduction', 'Ensure fold is clearly visible'],
    bodySiteCode: 'upperLegLeft'
  },
  {
    id: 'right-inguinal',
    name: 'Right Inguino-crural Fold',
    number: 11,
    instructions: [
      'Capture right inguino-crural fold + proximal thigh',
      'Include groin fold',
      'Include upper medial thigh'
    ],
    tips: ['Mirror positioning of left side', 'Maintain consistent framing'],
    bodySiteCode: 'upperLegRight'
  },
  {
    id: 'genital',
    name: 'Genital Area',
    number: 12,
    instructions: [
      'Capture carefully respecting privacy',
      'Include pubic region if lesions present',
      'Include vulvar/scrotal area if involved',
      'Include perineum if involved'
    ],
    tips: [
      'Avoid unnecessary exposure outside the lesion area',
      'Prioritize patient dignity and comfort'
    ],
    bodySiteCode: 'genital'
  }
];
